import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormControlLabel,
	FormGroup,
	IconButton,
	InputAdornment,
	Tooltip,
	Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { type ChangeEvent, type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRecommendationsContext } from "../../contexts/Recommendations/RecommendationsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useWeekViewContext } from "../../contexts/WeekView";
import { useAgencyAvailability } from "../../hooks/useAgencyAvailability";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useIncentiveConfigurations } from "../../hooks/useIncentiveConfigurations";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { agencySourceLabel } from "../../utils/agencyIntegrations";
import { getDepartmentPostDelayHours, MIN_AGENCY_POST_DELAY_HOURS, parseDelayHours } from "../../utils/agencyPostDelay";
import { getAgencySubRoleInfo } from "../../utils/agencySubRoles";
import {
	type AgencySource,
	getRolePriority,
	hppdTargetForDate,
	NurseFullRole,
	NurseFullRoleShort,
	type NurseRole,
	type PostShiftDetails,
	type PostShiftExtraData,
	RecommendationType,
	RequestSource,
} from "../../utils/models";
import { isOpenShiftDurationAllowed } from "../../utils/openShiftDuration";
import { NursePanelHeader } from "../../utils/panelComponents";
import { resolveShiftTimeOptions } from "../../utils/shiftTimeOptions";
import { getUniqueLocations } from "../../utils/utilFunctions";
import { useCheckOverlappingShiftsAndPatch } from "../staffing/hooks/useHandleCheckOverlappingShifts";
import { PostToAgencyToggle } from "../staffing/PostToAgencyToggle";
import { SystemSwitch } from "../UIToolkit/Clickable/SystemSwitch";
import { DelayHoursField } from "../UIToolkit/Inputs/DelayHoursField";
import { type DropdownItem, SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";
import { InfoIconWithTooltip } from "../UIToolkit/molecules/InfoIconWithTooltip";
import { CreateShiftRow } from "./CreateShiftRow";
import { useGetDepartmentSubRoles } from "./useGetDepartmentSubRoles";

// Sentinel value for the synthetic "Flat Bonus" dropdown option (old free-form bonus flow).
// Distinct from "" because MUI Select treats empty string as "unselected" and won't render its label.
const FLAT_BONUS_VALUE = "flat-bonus";

// The panel fills the staffing-changes modal so the two lists can share a fixed height: 90vh is the
// modal's max height and 48px its tab bar, leaving this for the panel (header + lists + footer).
const POST_SHIFT_PANEL_HEIGHT = "calc(90vh - 48px)";
// Recommendations is capped so it can't take the whole panel; whatever it doesn't use goes to manual
// entry, which fills the rest. Each list scrolls when its own content exceeds its share.
const RECOMMENDATIONS_MAX_SHARE = "60%";

interface PostShiftRequest {
	id: string;
	roleGroups: string[];
	jobId?: string;
	amount: number;
	startTime: DateTime | null;
	endTime: DateTime | null;
	isRecommendation: boolean;
	isChecked: boolean;
	location?: string;
	subRole?: NurseRole;
}

const isRequestValid = (request: Omit<PostShiftRequest, "id" | "isRecommendation" | "isChecked">, subRoleOptions: NurseRole[] = []): boolean => {
	return (
		(request.roleGroups.length > 0 || !!request.jobId) &&
		request.amount > 0 &&
		!!request.startTime &&
		request.startTime.isValid &&
		!!request.endTime &&
		request.endTime.isValid &&
		isOpenShiftDurationAllowed(request.startTime, request.endTime) &&
		(subRoleOptions.length === 0 || !!request.subRole)
	);
};

/**
 * Whole calendar days from `from` to `to`, by the date each one shows. The picker and the panel's date
 * can sit in different zones, so comparing instants could land a day off; comparing dates cannot.
 */
const calendarDaysBetween = (from: DateTime, to: DateTime): number =>
	DateTime.utc(to.year, to.month, to.day).diff(DateTime.utc(from.year, from.month, from.day), "days").days;

interface CreateShiftContentProps {
	shiftNo: number;
	setShiftNo?: Dispatch<SetStateAction<number>>;
	weekIndex: number;
	dayNo: number;
	isMasterSchedule: boolean;
	onClose?: (success?: boolean) => void;
	role?: `${NurseRole}` | "default";
	onSelectedDateChange?: (date: DateTime) => void;
	defaultLocation?: string;
	defaultJobId?: string;
	defaultRoles?: NurseFullRole[];
	defaultLockedTimes?: { startTime: DateTime; endTime: DateTime };
	hideRecommendations?: boolean;
}

const getRoleGroupsForRoles = (roleGroups: Record<string, string[]>, roles?: NurseFullRole[]): string[] => {
	if (!roles || roles.length === 0) {
		return [];
	}
	const relevantGroups: string[] = [];
	for (const [groupName, groupRoles] of Object.entries(roleGroups)) {
		if (roles.every((role) => groupRoles.includes(NurseFullRoleShort[role]))) {
			relevantGroups.push(groupName);
		}
	}
	return relevantGroups;
};

export const CreateShiftContent: FC<CreateShiftContentProps> = ({
	shiftNo,
	weekIndex,
	dayNo,
	isMasterSchedule,
	onClose,
	role = "default",
	onSelectedDateChange,
	defaultLocation,
	defaultJobId,
	defaultRoles,
	defaultLockedTimes,
	hideRecommendations,
}) => {
	const { chosenDepartment, timezone, calculateShiftTimes, hospitalData, isSnf, isAnyDemo, isRealDemo, getShiftDate, getWeekIndexByDate, isMedicalDepartment } =
		useHospitalData();
	// isRealDemo covers inhouse2__ demos, which isAnyDemo misses.
	const isAgencyDemo = isAnyDemo || isRealDemo;
	const isPickupByLocation = useFeatureFlag(FEATURE_FLAGS.PickupByLocation);
	const showLocationSelector = isPickupByLocation && !isMedicalDepartment;
	const availableLocations = useMemo(
		() => (showLocationSelector ? getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location) : []),
		[showLocationSelector, chosenDepartment],
	);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const isShiftBonusesEnabled = useFeatureFlag(FEATURE_FLAGS.EnableShiftBonuses);
	const { track } = useAnalytics();
	const { weekViewData } = useWeekViewContext();
	const { checkForOverlapsAndMergeIfUserApproved } = useCheckOverlappingShiftsAndPatch();
	const { recommendations } = useRecommendationsContext();
	const { t } = useTranslation();
	const [selectedDate, setSelectedDate] = useState<DateTime>(
		hospitalData.weeks.at(weekIndex)?.weeklyShifts[shiftNo]?.days.at(dayNo)?.date || DateTime.now().setZone(timezone),
	);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [createMore, setCreateMore] = useState<boolean>(false);
	const [extraPay, setExtraPay] = useState<boolean>(false);
	const [isExtraPayPercentage, setIsExtraPayPercentage] = useState<boolean>(false);
	const [extraPayAmount, setExtraPayAmount] = useState<number>(0);
	const [extraPayString, setExtraPayString] = useState<string>("");
	const [selectedIncentivePlanId, setSelectedIncentivePlanId] = useState<string>(FLAT_BONUS_VALUE);
	const { showSnackbar } = useSnackbar();
	const { data: incentivePlans = [] } = useIncentiveConfigurations(chosenDepartment.department_id);
	const selectedIncentivePlan = useMemo(() => incentivePlans.find((p) => p.id === selectedIncentivePlanId), [incentivePlans, selectedIncentivePlanId]);
	// Synthetic "Flat Bonus" option (default, old free-form flow with an amount input).
	const isSyntheticFlatBonus = selectedIncentivePlanId === FLAT_BONUS_VALUE;
	const isConfiguredPlan = !!selectedIncentivePlan;
	const incentivePlanItems = useMemo<DropdownItem<string>[]>(
		() => [
			{ value: FLAT_BONUS_VALUE, label: t("incentive.plan.flat.bonus"), id: FLAT_BONUS_VALUE },
			...incentivePlans.map((plan) => ({ value: plan.id, label: plan.name, id: plan.id })),
		],
		[incentivePlans, t],
	);
	// Per-window breakdown for the configured-plan chip:
	//   - First row: "Current bonus: $X" (either the first window's value or, if no windows, the plan's baseValue).
	//   - Later rows: "After Nh bonus: $X" for each subsequent window.
	const configuredPlanBreakdown = useMemo<{ label: string; amount: number }[]>(() => {
		if (!selectedIncentivePlan) return [];
		const sorted = [...selectedIncentivePlan.plans].sort((a, b) => a.startHour - b.startHour);
		if (sorted.length === 0) {
			// Flat configured plan — show its base value as the "current bonus".
			return [{ label: t("incentive.current.bonus.label"), amount: selectedIncentivePlan.baseValue }];
		}
		return sorted.map((window) => ({
			label: window.startHour === 0 ? t("incentive.current.bonus.label") : t("incentive.after.hours.bonus.label", { hours: window.startHour }),
			amount: window.value,
		}));
	}, [selectedIncentivePlan, t]);

	const postShiftRecommendation = useMemo(() => {
		return recommendations.find(
			(rec) =>
				rec.type === RecommendationType.PostShift &&
				rec.date.hasSame(selectedDate, "day") &&
				rec.extra_data &&
				Array.isArray((rec.extra_data as PostShiftExtraData).data) &&
				(rec.extra_data as PostShiftExtraData).data.length > 0,
		);
	}, [recommendations, selectedDate]);

	const initialShiftTimes = useMemo(() => {
		const currentDateWeekIndex = getWeekIndexByDate(selectedDate) ?? weekIndex;
		const currentDateDayNo = selectedDate.weekday % 7;

		return calculateShiftTimes(hospitalData.weeks[currentDateWeekIndex].weeklyShifts[shiftNo].days[currentDateDayNo], shiftNo, undefined);
	}, [calculateShiftTimes, hospitalData, selectedDate, weekIndex, shiftNo, getWeekIndexByDate]);

	const [postShiftRequests, setPostShiftRequests] = useState<PostShiftRequest[]>([]);
	const createEmptyManualEntry = useCallback(
		(isFirst = false): PostShiftRequest => {
			const foundRoleGroups = getRoleGroupsForRoles(chosenDepartment.role_groups, defaultRoles);
			return {
				id: `manual-${uuidv4()}`,
				roleGroups: isFirst && defaultRoles ? foundRoleGroups : [],
				amount: 1,
				startTime: isFirst && defaultLockedTimes ? defaultLockedTimes.startTime : null,
				endTime: isFirst && defaultLockedTimes ? defaultLockedTimes.endTime : null,
				isRecommendation: false,
				// A caller that hides recommendations and prefills who the shift is for (roles, or a job when
				// job management is on) is posting that one slot, so the row starts ticked and Create is ready.
				// Job-section gap clicks carry a job but no roles, so the job alone must be enough to tick it.
				isChecked: isFirst && (!!defaultRoles || (jobManagementEnabled && !!defaultJobId)) && !!hideRecommendations,
				location: isFirst ? defaultLocation : undefined,
				jobId: isFirst && jobManagementEnabled ? defaultJobId : undefined,
			};
		},
		[chosenDepartment.role_groups, defaultRoles, defaultLockedTimes, defaultLocation, defaultJobId, jobManagementEnabled, hideRecommendations],
	);
	const [manualEntries, setManualEntries] = useState<PostShiftRequest[]>(() => [createEmptyManualEntry(true)]);

	const creationAmount = postShiftRequests.filter((r) => r.isChecked).length + manualEntries.filter((e) => e.isChecked).length;
	// Only split the panel into two filled panes when recommendations are present. With no
	// recommendations the panel sizes to its content so a lone manual entry isn't a huge empty box.
	const showRecommendations = !hideRecommendations && postShiftRequests.length > 0;

	useEffect(() => {
		if (postShiftRecommendation && !hideRecommendations) {
			const recommendationData = (postShiftRecommendation.extra_data as PostShiftExtraData).data;
			const priorityOf = (details: PostShiftDetails): number => {
				const groups = details.recommended_role_groups ?? [];
				return Math.max(0, ...groups.map((group) => getRolePriority(chosenDepartment.role_groups?.[group] ?? [])));
			};
			const sortedData = [...recommendationData].sort((a, b) => {
				const priorityDelta = priorityOf(b) - priorityOf(a);
				if (priorityDelta !== 0) return priorityDelta;
				return DateTime.fromISO(a.recommended_start_time).toMillis() - DateTime.fromISO(b.recommended_start_time).toMillis();
			});
			const requests: PostShiftRequest[] = sortedData.map((data: PostShiftDetails, index) => ({
				id: `recommendation-${index}`,
				roleGroups: data.recommended_role_groups || [],
				jobId: jobManagementEnabled && data.recommended_job_id ? data.recommended_job_id : undefined,
				amount: data.recommended_staff_count || 0,
				startTime: data.recommended_start_time ? DateTime.fromISO(data.recommended_start_time) : initialShiftTimes.startTime,
				endTime: data.recommended_end_time ? DateTime.fromISO(data.recommended_end_time) : initialShiftTimes.endTime,
				isRecommendation: true,
				isChecked: true,
			}));
			setPostShiftRequests(requests);
		} else {
			setPostShiftRequests([]);
		}
	}, [postShiftRecommendation, initialShiftTimes, hideRecommendations, jobManagementEnabled, chosenDepartment.role_groups]);

	const toggleExtraPay = useCallback(() => {
		setExtraPay((prev) => !prev);
	}, []);
	const toggleExtraPayPercentage = useCallback(() => {
		setIsExtraPayPercentage((prev) => !prev);
	}, []);

	const [chargeOnly, setChargeOnly] = useState<boolean>(false);
	const [postToAgency, setPostToAgency] = useState<boolean>(false);
	const delayedAgencyPostingEnabled = useFeatureFlag(FEATURE_FLAGS.DelayedAgencyPostingEnabled);
	const [postWithDelay, setPostWithDelay] = useState<boolean>(true);
	const [postDelayHours, setPostDelayHours] = useState<string>(String(getDepartmentPostDelayHours(chosenDepartment)));
	const [selectedAgency, setSelectedAgency] = useState<string | null>(null);
	const { data: subRoleCatalog = [] } = useGetDepartmentSubRoles(chosenDepartment.department_id);
	const { availableAgencies, agencyFacilityIdMap } = useAgencyAvailability();
	// `PostToAgencyToggle` and `agencySourceName` both speak in display labels, not enum values.
	const availableAgencyLabels = useMemo(() => availableAgencies.map(agencySourceLabel), [availableAgencies]);

	// `PostToAgencyToggle` only renders the agency picker when there are multiple available agencies,
	// so `selectedAgency` can be null even when posting — fall back to the single available agency.
	// Kept LABEL-typed: `resolvedAgencySource` lowercases it back to the enum, and `agencySourceName`
	// is sent (and persisted) as the label.
	const effectiveAgencySource = useMemo(() => selectedAgency ?? availableAgencyLabels[0] ?? null, [selectedAgency, availableAgencyLabels]);
	const resolvedAgencySource = useMemo(() => (effectiveAgencySource ? (effectiveAgencySource.toLowerCase() as AgencySource) : null), [effectiveAgencySource]);

	// Passing `agencySource: null` when not posting preserves the create-flow-only guard (no options
	// unless the agency toggle is on and an agency is resolved).
	const getSubRoleInfoForRow = useCallback(
		(row: { jobId?: string; roleGroups: string[] }): { options: NurseRole[]; defaultValue: NurseRole | undefined } =>
			getAgencySubRoleInfo({
				catalog: subRoleCatalog,
				agencySource: postToAgency ? resolvedAgencySource : null,
				jobManagementEnabled,
				roleGroups: chosenDepartment.role_groups,
				row,
			}),
		[postToAgency, resolvedAgencySource, subRoleCatalog, jobManagementEnabled, chosenDepartment.role_groups],
	);

	const isRowValid = useCallback((row: PostShiftRequest) => isRequestValid(row, getSubRoleInfoForRow(row).options), [getSubRoleInfoForRow]);
	const isAllValid = useMemo(() => {
		return (
			(postShiftRequests.every((r) => !r.isChecked) || postShiftRequests.every((r) => (r.isChecked ? isRowValid(r) : true))) &&
			manualEntries.every((e) => (e.isChecked ? isRowValid(e) : true))
		);
	}, [postShiftRequests, manualEntries, isRowValid]);
	const allManualEntriesValid = useMemo(() => manualEntries.every((e) => isRowValid(e)), [manualEntries, isRowValid]);

	// Parsed once so the button, the payloads and analytics all use the same value. Only relevant
	// when the delay is actually applied; otherwise whatever is typed is ignored.
	const delayApplies = delayedAgencyPostingEnabled && postToAgency && postWithDelay;
	const parsedPostDelayHours = parseDelayHours(postDelayHours);
	const delayInvalid = delayApplies && parsedPostDelayHours === null;

	// Reset/auto-select the per-row sub_role whenever the inputs that drive its options change
	// (agency toggle, selected agency, the row's jobId, or the catalog). We only auto-fill the
	// default when the current value isn't in the new options, so the user's explicit pick is preserved.
	useEffect(() => {
		const reconcile = (rows: PostShiftRequest[]): PostShiftRequest[] => {
			let changed = false;
			const next = rows.map((row) => {
				const { options, defaultValue } = getSubRoleInfoForRow(row);
				if (options.length === 0) {
					if (row.subRole !== undefined) {
						changed = true;
						return { ...row, subRole: undefined };
					}
					return row;
				}
				if (row.subRole && options.includes(row.subRole)) return row;
				if (row.subRole === defaultValue) return row;
				changed = true;
				return { ...row, subRole: defaultValue };
			});
			return changed ? next : rows;
		};
		setManualEntries((prev) => reconcile(prev));
		setPostShiftRequests((prev) => reconcile(prev));
	}, [getSubRoleInfoForRow]);

	const expandSearchOptions = useMemo(
		() => [
			{
				key: "charge_only",
				label: t("allow.charge.only"),
				state: chargeOnly,
				setState: setChargeOnly,
			},
		],
		[t, chargeOnly],
	);
	const handleExtraPayAmountChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			let parsedValue = 0;
			if (value === "") {
				setExtraPayAmount(0);
				setExtraPayString("0");
			} else {
				parsedValue = Number.parseFloat(value);
				if (Number.isNaN(parsedValue) || parsedValue < 0) {
					setExtraPayString("0");
					return;
				}
				setExtraPayAmount(Number.parseFloat(parsedValue.toFixed(isExtraPayPercentage ? 0 : 2)));
			}
			setExtraPayString(!isExtraPayPercentage ? value : parsedValue.toFixed(0));
		},
		[isExtraPayPercentage],
	);

	const debouncedExtraPayAmount = useDebouncedValue(extraPayAmount, 700);

	useEffect(() => {
		setExtraPayString(debouncedExtraPayAmount.toFixed(isExtraPayPercentage ? 0 : 2));
	}, [debouncedExtraPayAmount, isExtraPayPercentage]);

	const updateRecommendationRequest = useCallback((id: string, updates: Partial<PostShiftRequest>) => {
		setPostShiftRequests((prev) => prev.map((req) => (req.id === id ? { ...req, ...updates } : req)));
	}, []);

	const updateManualEntry = useCallback((id: string, updates: Partial<Omit<PostShiftRequest, "id" | "isRecommendation">>) => {
		setManualEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, isChecked: true, ...updates } : entry)));
	}, []);

	const addManualEntry = useCallback(() => {
		setManualEntries((prev) => [...prev, createEmptyManualEntry()]);
	}, [createEmptyManualEntry]);

	const removeManualEntry = useCallback((id: string) => {
		setManualEntries((prev) => (prev.length <= 1 ? prev : prev.filter((entry) => entry.id !== id)));
	}, []);

	// A manual row's times are absolute: a gap click locks them to the clicked day, and editing the hours
	// pins them to the day shown then. Moving the panel to another day must carry them along, or the shift
	// still posts on the old day. Rows without times already follow the date through `initialShiftTimes`.
	const handleSelectedDateChange = useCallback(
		(date: DateTime) => {
			const days = calendarDaysBetween(selectedDate, date);
			if (days !== 0) {
				// Adding calendar days (not 24h blocks) keeps each row's wall-clock hours across a DST change.
				setManualEntries((prev) =>
					prev.map((entry) => ({
						...entry,
						startTime: entry.startTime ? entry.startTime.plus({ days }) : null,
						endTime: entry.endTime ? entry.endTime.plus({ days }) : null,
					})),
				);
			}
			setSelectedDate(date);
			onSelectedDateChange?.(date);
		},
		[selectedDate, onSelectedDateChange],
	);

	async function warnShorterShiftPossible(shiftTimes: { startTime: DateTime | null; endTime: DateTime | null }, roleGroups: string[], jobId?: string) {
		const day = hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo];
		// Get first role from first role group for timing calculation
		let roleForTiming = NurseFullRole.RN;
		if (roleGroups.length > 0) {
			const firstRoleGroup = roleGroups[0];
			const roles = chosenDepartment.role_groups[firstRoleGroup];
			if (roles && roles.length > 0) {
				roleForTiming = NurseFullRole[roles[0] as keyof typeof NurseFullRole];
			}
		}
		const defaultShiftTimes = calculateShiftTimes(day, shiftNo, roleForTiming);
		if (!shiftTimes.startTime || !shiftTimes.endTime) {
			return defaultShiftTimes;
		}
		if (defaultShiftTimes.startTime.valueOf() !== shiftTimes.startTime.valueOf() || defaultShiftTimes.endTime.valueOf() !== shiftTimes.endTime.valueOf()) {
			return shiftTimes;
		}

		// Was `partial_block_shifts[role] || []` with no `default` fallback, unlike EditTimePopover —
		// routing through the shared resolver fixes that alongside making it job-aware (DAT-580).
		const { partialBlockShifts: partialShiftHoursOptions } = resolveShiftTimeOptions({ department: chosenDepartment, jobManagementEnabled, jobId, role });

		const targetHours = isSnf ? hppdTargetForDate(chosenDepartment.hppd_target, day.date.weekday) : 4;
		const shortenShiftHours = partialShiftHoursOptions.find(
			(partialShiftHours: number) => (weekViewData[dayNo].dayStats.totalHours + partialShiftHours) / weekViewData[dayNo].dayStats.census > targetHours,
		);
		if (!shortenShiftHours) {
			return shiftTimes;
		}

		let changeShiftTimes = false;
		await showConfirmationDialog({
			title: t("shorter.shift.possible"),
			content: t("shorter.shift.question", { hours: shortenShiftHours }),
			actions: [
				{
					label: t("use.recommended.times"),
					onClick: () => {
						changeShiftTimes = true;
						return;
					},
				},
				{
					label: t("keep.existing.times"),
					onClick: () => {
						changeShiftTimes = false;
						return;
					},
				},
			],
		});

		if (changeShiftTimes) {
			return {
				...shiftTimes,
				endTime: shiftTimes.endTime.minus({ hours: shortenShiftHours }),
			};
		}

		return shiftTimes;
	}

	const resetFields = (): void => {
		setManualEntries([createEmptyManualEntry()]);
		setPostToAgency(false);
	};

	const handleCreatePostShift = async () => {
		// The button is disabled for this, but guard the handler too so no other caller can slip past.
		if (delayInvalid) return;

		const selectedRequests: PostShiftRequest[] = [];

		postShiftRequests.forEach((req) => {
			if (req.isChecked) {
				selectedRequests.push(req);
			}
		});

		manualEntries.forEach((entry) => {
			if (entry.isChecked) {
				selectedRequests.push(entry);
			}
		});

		if (selectedRequests.length === 0) {
			showSnackbar(t("error.select.roles.slots"), "error");
			return;
		}

		setIsLoading(true);

		try {
			for (const request of selectedRequests) {
				const roleGroups = request.roleGroups;
				const roles = Object.entries(chosenDepartment.role_groups)
					.filter(([key]) => roleGroups.includes(key as NurseFullRole))
					.flatMap(([_, roles]) => roles);
				const uniqueRoles = Array.from(new Set(roles));
				const selectedRoles = uniqueRoles.map<NurseFullRole>((roleShort) => NurseFullRole[roleShort as keyof typeof NurseFullRole]);

				track("Staffing - create open shift", {
					source: isMasterSchedule ? "master_schedule" : "staffing",
					week_index: weekIndex,
					day_no: dayNo,
					shift_no: shiftNo,
					date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
					roles: JSON.stringify(selectedRoles),
					jobId: request.jobId,
					needed_nurses: request.amount,
					changedFromMasterSchedule: isMasterSchedule,
					isRecommendation: request.isRecommendation,
					location: request.location || undefined,
					postToAgency: postToAgency && resolvedAgencySource !== null,
					agencySource: postToAgency ? (resolvedAgencySource ?? undefined) : undefined,
					subRole: postToAgency ? request.subRole : undefined,
					postWithDelay: delayApplies,
					agencyPostDelayHours: delayApplies ? (parsedPostDelayHours ?? undefined) : undefined,
				});

				let shiftTimes = {
					startTime: request.startTime,
					endTime: request.endTime,
				};
				shiftTimes = await warnShorterShiftPossible(shiftTimes, request.roleGroups, request.jobId);
				const licenseTypes: ("RN" | "LPN" | "CNA")[] = [];

				if (!jobManagementEnabled && !request.jobId) {
					if (selectedRoles.includes(NurseFullRole.RN)) {
						licenseTypes.push("RN");
					}
					if (selectedRoles.includes(NurseFullRole.LPN)) {
						licenseTypes.push("LPN");
					}
					if (selectedRoles.includes(NurseFullRole.CNA)) {
						licenseTypes.push("CNA");
					}

					if (!isAnyDemo && postToAgency && licenseTypes.length === 0) {
						throw new Error("error.creating.shift.invalidAgencyRoles");
					}
				}

				if (!shiftTimes.startTime || !shiftTimes.endTime) {
					showSnackbar(t("error.invalid.shift.times"), "error");
					throw new Error("Invalid shift times");
				}

				await checkForOverlapsAndMergeIfUserApproved({
					shiftStart: shiftTimes.startTime,
					shiftEnd: shiftTimes.endTime,
					selectedRoles,
					jobId: jobManagementEnabled ? request.jobId : undefined,
					neededNurses: request.amount,
					chargeOnly: chargeOnly,
					bonus: extraPayAmount,
					incentiveConfigurationId: isSyntheticFlatBonus ? undefined : selectedIncentivePlanId,
					incentiveConfigurationPlans: isSyntheticFlatBonus ? undefined : selectedIncentivePlan?.plans,
					isMasterSchedule,
					panel: "create-desktop",
					location: request.location || undefined,
					postToAgency: postToAgency && resolvedAgencySource !== null,
					// Backend derives the posted Nursa license from the selected per-row sub_role
					// (with role/job-derived fallback), so the FE no longer sends a license override.
					agencyLicenseTypes: undefined,
					agencyFacilityIds:
						postToAgency && resolvedAgencySource && agencyFacilityIdMap[resolvedAgencySource]
							? [agencyFacilityIdMap[resolvedAgencySource] as string]
							: undefined,
					agencySource: postToAgency && resolvedAgencySource ? [resolvedAgencySource] : undefined,
					agencySourceName: postToAgency && effectiveAgencySource ? effectiveAgencySource : undefined,
					subRole: postToAgency ? request.subRole : undefined,
					postWithDelay: delayApplies,
					agencyPostDelayHours: delayApplies ? (parsedPostDelayHours ?? undefined) : undefined,
					actionIfMergeCanceled: () => {},
					requestSource: request.isRecommendation ? RequestSource.PostShiftRecommendation : RequestSource.PostShift,
					actionAfterMerge: (result) => {
						track("Staffing - update needed nurses open shift", {
							details: JSON.stringify({
								source: isMasterSchedule ? "master_schedule" : "staffing",
								week_index: weekIndex,
								day_no: dayNo,
								shift_no: shiftNo,
								date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
								old_needed_nurses: result?.oldNurseAmount ?? 0,
								new_needed_nurses: request.amount,
							}),
						});
					},
					actionsWhenMergeNotNeeded: {
						onSuccess: async () => {},
						onError: () => {},
					},
				});
			}

			// All requests processed
			if (createMore) {
				resetFields();
				setIsLoading(false);
			} else {
				onClose?.(true);
			}
		} catch (error) {
			setIsLoading(false);
			const errorMessage = (error as { message?: unknown } | null)?.message;
			const messageKey = typeof errorMessage === "string" ? errorMessage : null;

			if (messageKey?.startsWith("error.")) {
				// If the error message is a translation key, use it directly.
				showSnackbar(t(messageKey), "error");
			} else {
				// Fallback to the generic error message.
				showSnackbar(t("error.creating.shift"), "error");
			}
		}
	};

	return (
		<Box
			data-testid="post-shift-panel"
			sx={{
				display: "flex",
				flexDirection: "column",
				height: showRecommendations ? POST_SHIFT_PANEL_HEIGHT : undefined,
				maxHeight: POST_SHIFT_PANEL_HEIGHT,
			}}
		>
			<NursePanelHeader
				changeDate={true}
				onSelectedDateChanged={handleSelectedDateChange}
				shiftNo={shiftNo}
				weekIndex={weekIndex}
				dayNo={dayNo}
				isMasterSchedule={isMasterSchedule}
				timezone={timezone}
				title={t("post.shift")}
				allowEditShiftTimes={false}
				allowPastDateSelection={false}
			/>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
					minHeight: 0,
				}}
			>
				<Box sx={{ p: 2, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
					{showRecommendations && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 2,
								mb: 3,
								pr: 1,
								// pb absorbs the field's focus-outline overhang below the last row so a full list doesn't
								// show a sliver scrollbar. Sizes to content but never past RECOMMENDATIONS_MAX_SHARE of the
								// panel, then scrolls; unused space falls through to manual entry. The section is the scroll
								// container so short lists show no scrollbar and the last row is always reachable.
								pb: 1,
								flex: "0 0 auto",
								minHeight: 0,
								maxHeight: RECOMMENDATIONS_MAX_SHARE,
								overflowY: "auto",
							}}
						>
							<Typography sx={{ color: "#1A1A1A", fontSize: "16px", fontWeight: 600, mb: 2 }}>{t("recommendations")}</Typography>
							{postShiftRequests.map((request) => (
								<CreateShiftRow
									key={request.id}
									roleGroups={request.roleGroups}
									setRoleGroups={(roleGroups) => {
										if (Array.isArray(roleGroups)) {
											updateRecommendationRequest(request.id, {
												roleGroups,
												// Role-group mode drives sub-roles off the selected groups (no jobId), so changing the
												// groups resets the picked sub-role to the new default — mirroring setJobId in job mode.
												...(jobManagementEnabled ? {} : { subRole: getSubRoleInfoForRow({ roleGroups }).defaultValue }),
											});
										}
									}}
									{...(jobManagementEnabled
										? {
												jobId: request.jobId,
												setJobId: (jobId: string) =>
													updateRecommendationRequest(request.id, {
														jobId,
														subRole: getSubRoleInfoForRow({ jobId, roleGroups: request.roleGroups }).defaultValue,
													}),
											}
										: {})}
									amount={request.amount}
									setAmount={(amount) => {
										if (amount !== undefined) {
											updateRecommendationRequest(request.id, { amount });
										}
									}}
									times={{ startTime: request.startTime, endTime: request.endTime }}
									updateTimes={(startTime, endTime) => updateRecommendationRequest(request.id, { startTime, endTime })}
									isChecked={request.isChecked}
									setIsChecked={(isChecked) => updateRecommendationRequest(request.id, { isChecked })}
									isValid={isRowValid(request)}
									subRoleOptions={getSubRoleInfoForRow(request).options}
									subRole={request.subRole}
									setSubRole={(subRole) => updateRecommendationRequest(request.id, { subRole })}
								/>
							))}
						</Box>
					)}
					{showRecommendations && <Divider sx={{ my: 3, flexShrink: 0 }} />}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
							pr: 1,
							// When recommendations are present, fill the leftover space; otherwise size to content so a
							// short list stays compact. Either way the section scrolls so the last entry is reachable.
							flex: showRecommendations ? "1 1 0" : "0 1 auto",
							minHeight: 0,
							overflowY: "auto",
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<Typography sx={{ color: "#1A1A1A", fontSize: "16px", fontWeight: 600 }}>{t("manual.entry")}</Typography>
						</Box>
						{manualEntries.map((entry, index) => (
							<Box key={entry.id} sx={{ display: "flex", alignItems: "center" }}>
								<CreateShiftRow
									// Manual entry only, and only the first: the recommendations list above is empty
									// unless the day has a live recommendation, but there is always one manual row.
									data-annie-target={index === 0 ? "post-shift-entry-row" : undefined}
									roleGroups={entry.roleGroups}
									setRoleGroups={(roleGroups) => {
										if (Array.isArray(roleGroups)) {
											updateManualEntry(entry.id, {
												roleGroups,
												// Role-group mode drives sub-roles off the selected groups (no jobId), so changing the
												// groups resets the picked sub-role to the new default — mirroring setJobId in job mode.
												...(jobManagementEnabled ? {} : { subRole: getSubRoleInfoForRow({ roleGroups }).defaultValue }),
											});
										}
									}}
									{...(jobManagementEnabled
										? {
												jobId: entry.jobId,
												setJobId: (jobId: string) =>
													updateManualEntry(entry.id, { jobId, subRole: getSubRoleInfoForRow({ jobId, roleGroups: entry.roleGroups }).defaultValue }),
											}
										: {})}
									amount={entry.amount}
									setAmount={(amount) => {
										if (amount !== undefined) {
											updateManualEntry(entry.id, { amount });
										}
									}}
									times={{ startTime: entry.startTime, endTime: entry.endTime }}
									fallbackTimes={initialShiftTimes}
									updateTimes={(startTime, endTime) => updateManualEntry(entry.id, { startTime, endTime })}
									isChecked={entry.isChecked}
									setIsChecked={(isChecked) => updateManualEntry(entry.id, { isChecked })}
									isValid={isRowValid(entry)}
									location={entry.location}
									setLocation={showLocationSelector ? (location) => updateManualEntry(entry.id, { location }) : undefined}
									availableLocations={showLocationSelector ? availableLocations : undefined}
									subRoleOptions={getSubRoleInfoForRow(entry).options}
									subRole={entry.subRole}
									setSubRole={(subRole) => updateManualEntry(entry.id, { subRole })}
								/>
								{manualEntries.length > 1 && (
									<IconButton size="small" onClick={() => removeManualEntry(entry.id)} sx={{ ml: 1, color: "#EF4444" }}>
										<RemoveCircleOutlineIcon fontSize="small" />
									</IconButton>
								)}
								{index === manualEntries.length - 1 && (
									<Tooltip title={!allManualEntriesValid ? t("manual.entry.invalid.fields") : ""}>
										<Box>
											<IconButton
												size="small"
												data-testid="create-shift-add-manual-entry-button"
												onClick={addManualEntry}
												disabled={!allManualEntriesValid}
												sx={{ ml: 1, color: "primary.main", "&.Mui-disabled": { color: "#E1E6EF" } }}
											>
												<AddCircleOutlineIcon fontSize="small" />
											</IconButton>
										</Box>
									</Tooltip>
								)}
							</Box>
						))}
					</Box>
				</Box>

				{/* Action Buttons */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						gap: 2,
						bgcolor: "#FFFFFF",
						p: 2,
						flexShrink: 0,
						borderTop: "1px solid #E1E6EF",
					}}
				>
					<Box>
						<FormGroup
							data-annie-target="post-shift-options"
							sx={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								gap: 4,
								mt: 0.5,
								flexWrap: "wrap",
							}}
						>
							{expandSearchOptions
								.filter(({ key }) => key !== "post_to_nursa")
								.map(({ key, label, state, setState }) => (
									<FormControlLabel
										key={key}
										data-testid={`post-shift-${key}-toggle`}
										sx={{ m: 0 }}
										control={
											<Checkbox
												checked={state}
												onChange={(e) => setState(e.target.checked)}
												sx={{
													color: "primary.main",
													"&.Mui-checked": { color: "primary.main" },
												}}
											/>
										}
										label={label}
									/>
								))}
							{(isAgencyDemo || availableAgencies.length > 0) && (
								<PostToAgencyToggle
									checked={postToAgency}
									onChange={setPostToAgency}
									selectedAgency={selectedAgency}
									onAgencyChange={setSelectedAgency}
									agencies={availableAgencyLabels}
								/>
							)}
							{delayedAgencyPostingEnabled && postToAgency && (isAgencyDemo || availableAgencies.length > 0) && (
								<DelayHoursField
									checked={postWithDelay}
									onCheckedChange={setPostWithDelay}
									checkboxLabel={t("post.with.delay")}
									value={postDelayHours}
									onChange={setPostDelayHours}
									suffixLabel={t("hours.before.shift.start")}
									invalidHelperText={t("agency.post.delay.invalid", { min: MIN_AGENCY_POST_DELAY_HOURS })}
									minHours={MIN_AGENCY_POST_DELAY_HOURS}
									dataTestId="post-shift-agency-delay"
								/>
							)}
							{(isShiftBonusesEnabled || isAnyDemo) && (
								<>
									<FormControlLabel
										data-testid="post-shift-extra-pay-toggle"
										sx={{ m: 0 }}
										control={
											<Checkbox
												checked={extraPay}
												onChange={toggleExtraPay}
												sx={{
													color: "primary.main",
													"&.Mui-checked": { color: "primary.main" },
												}}
											/>
										}
										label={t("incentive.pay")}
									/>
									{extraPay && (
										<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2, ml: 4 }}>
											<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2 }}>
												{isAnyDemo && (
													<SystemSwitch label={t("by.percentage")} disabled={!extraPay} checked={isExtraPayPercentage} onChange={toggleExtraPayPercentage} />
												)}
												<SystemDropdown
													label={t("incentive.plan")}
													items={incentivePlanItems}
													selectedItem={selectedIncentivePlanId}
													onChange={(value) => setSelectedIncentivePlanId(value ?? FLAT_BONUS_VALUE)}
													formControlSx={{ minWidth: 200 }}
													data-testid="post-shift-incentive-plan"
												/>
												{isSyntheticFlatBonus && (
													<SystemValidationTextField
														disabled={!extraPay}
														variant={"outlined"}
														reserveHeightForHelperText={false}
														label={isExtraPayPercentage ? t("percentage") : t("amount")}
														placeholder={isExtraPayPercentage ? "%" : "$"}
														inputType={"number"}
														value={extraPayString}
														onChange={handleExtraPayAmountChange}
														maxValue={1000}
														inputProps={{
															endAdornment: (
																<InputAdornment position={"end"}>
																	{isExtraPayPercentage ? <PercentOutlinedIcon fontSize={"small"} /> : <AttachMoneyOutlinedIcon fontSize={"small"} />}
																</InputAdornment>
															),
														}}
													/>
												)}
												{isConfiguredPlan && configuredPlanBreakdown.length > 0 && (
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
															px: 1.5,
															py: 0.75,
															bgcolor: "background.paper",
															border: "1px solid",
															borderColor: "divider",
															borderRadius: 1,
														}}
													>
														<InfoIconWithTooltip title={t("incentive.fast.grab.chip.tooltip")} />
														<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
															{configuredPlanBreakdown.map((row) => (
																<Typography key={row.label} sx={{ fontSize: "0.75rem" }}>
																	<strong>{row.label}:</strong>&nbsp;${row.amount.toFixed(2)}
																</Typography>
															))}
														</Box>
													</Box>
												)}
											</Box>
										</Box>
									)}
								</>
							)}
							<FormControlLabel
								data-testid="post-shift-create-more-toggle"
								sx={{ m: 0 }}
								control={
									<Checkbox
										checked={createMore}
										onChange={(e) => setCreateMore(e.target.checked)}
										sx={{
											color: "primary.main",
											"&.Mui-checked": { color: "primary.main" },
										}}
									/>
								}
								label={t("create.more")}
							/>
						</FormGroup>
					</Box>
					<Box sx={{ display: "flex", gap: 3 }}>
						<Button
							data-testid={"post-shift-cancel-button"}
							variant="outlined"
							onClick={() => onClose?.(false)}
							sx={{
								textTransform: "none",
								borderColor: "#E1E6EF",
								color: "primary.dark",
								borderRadius: "8px",
								px: 3,
								height: "40px",
								"&:hover": {
									borderColor: "primary.main",
									bgcolor: "transparent",
								},
							}}
						>
							{t("cancel")}
						</Button>
						<Button
							data-testid={"post-shift-create-button"}
							data-annie-target="post-shift-submit"
							variant="contained"
							onClick={handleCreatePostShift}
							disabled={!isAllValid || isLoading || creationAmount === 0 || delayInvalid}
							sx={{
								textTransform: "none",
								bgcolor: "primary.main",
								borderRadius: "8px",
								color: "white",
								height: "40px",
								"&:hover": {
									bgcolor: "primary.dark",
								},
								px: 3,
								"&.Mui-disabled": {
									bgcolor: "#E1E6EF",
									color: "primary.main",
								},
							}}
						>
							{isLoading ? t("creating") : `${creationAmount} ${t("create")}`}
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export function showConfirmationDialog({
	title,
	content,
	actions,
}: {
	title: string;
	content: string;
	actions: { label: string; onClick: () => void }[];
}): Promise<void> {
	return new Promise((resolve) => {
		const dialog = document.createElement("div");
		document.body.appendChild(dialog);

		const root = createRoot(dialog);
		root.render(
			<Dialog
				data-testid={`confirmation-dialog-${title}`}
				open={true}
				sx={{
					"& .MuiDialog-paper": {
						borderRadius: "12px",
						maxWidth: "550px",
						width: "100%",
					},
				}}
			>
				<DialogTitle
					sx={{
						fontSize: "18px",
						fontWeight: 600,
						color: "#1A1A1A",
						pt: 3,
						px: 3,
						textAlign: "center",
					}}
				>
					{title}
				</DialogTitle>
				<DialogContent sx={{ px: 3 }}>
					<DialogContentText
						sx={{
							color: "text.secondary",
							textAlign: "center",
						}}
					>
						{content}
					</DialogContentText>
				</DialogContent>
				<DialogActions
					sx={{
						p: 3,
						display: "flex",
						justifyContent: "center",
						gap: 2,
						"& .MuiButton-root": {
							flex: 1,
							maxWidth: "250px",
						},
					}}
				>
					{actions.map((action, index) => (
						<Button
							data-testid={`confirmation-dialog-${title}-${action.label}`}
							key={index}
							onClick={() => {
								action.onClick();
								root.unmount();
								document.body.removeChild(dialog);
								resolve();
							}}
							variant={index === actions.length - 1 ? "contained" : "outlined"}
							sx={{
								textTransform: "none",
								borderRadius: "8px",
								px: 3,
								height: "40px",
								whiteSpace: "nowrap",
								...(index === actions.length - 1
									? {
											bgcolor: "primary.main",
											color: "white",
											"&:hover": {
												bgcolor: "primary.dark",
											},
										}
									: {
											borderColor: "#E1E6EF",
											color: "primary.dark",
											"&:hover": {
												borderColor: "primary.main",
												bgcolor: "transparent",
											},
										}),
							}}
						>
							{action.label}
						</Button>
					))}
				</DialogActions>
			</Dialog>,
		);
	});
}
